import React, { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import {
    DEAL_PRODUCT_TYPE,
    SESSION_COOKIE_NAME,
    CHECKOUT_FINANCE_URL,
    CHECKOUT_CASH_URL,
    PxVersion,
} from '../../constants/main';
import { stepCodes, useUpdateStepCode } from '@hooks/useUpdateStepCode';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useFinanceWidgetDuck } from '@hooks/useFinanceWidgetDuck';
import { useJourneyType } from '@hooks/useJourneyType';
import { useTranslations } from '@hooks/useTranslations';
import { useGTM } from '@hooks/useGTM';
import { CASH, DealService, FINANCE } from '../../services';
import { getSessionIdCookie } from '@utils/Session.utils';
import { handleFinanceQuote, updateGeneralContext } from '@utils/getInitialProps/common';
import CheckoutDelivery from '@components/CheckoutDelivery';
import CheckoutStepWizard from '@components/CheckoutStepWizard';
import Main from '@components/Layout/Main';
import RedirectionModal from '@components/RedirectionModal';
import { batch, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import FilterDuck from '../../redux/filters/filter.duck';
import { MySavedDealJourneyType } from '../../redux/user/user.duck.interface';
import { PaymentJourneyTypes } from '../../partExchange/interfaces/Default';
import { constructPsaCheckoutUrl } from '@utils/Deal.utils';
import UserDuck from '../../redux/user/user.duck';
import { ContextWithStore } from '@utils/getInitialProps/@types';
import carDetailsDuck from 'src/redux/carDetails/carDetails.duck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { syncPartExchange } from '../../partExchange/service/ApiService';
import ConfiguratorDuck from '../../redux/configurator/configurator.duck';
import { processMopData } from '../../partExchange/utility/general';

interface CheckoutProps {
    isEC41Car?: boolean;
}
function Checkout({}: CheckoutProps) {
    const { t } = useTranslations();
    const { pushDeliveryPageLoad, pushCheckoutEvent } = useGTM();
    const router = useRouter();

    useUpdateStepCode(undefined, stepCodes.dealer);

    const dispatch = useDispatch();
    const { paymentJourneyType } = useJourneyType();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { financeQuote } = useFinanceWidgetDuck();

    const isEC41Journey = currentDeal?.productType === DEAL_PRODUCT_TYPE.CHILD;

    const goToMyDetails = useCallback(() => {
        dispatch(UIDuck.setLoading(5));
        router.push('/checkout/my-details');
    }, [router, dispatch]);

    const handleLoading = useCallback(
        (x: number) => () => {
            dispatch(UIDuck.setLoading(x));
        },
        [dispatch]
    );

    const isBasketEmpty =
        isEmpty(carDetails) || (paymentJourneyType === PaymentJourneyTypes.FINANCE && isEmpty(financeQuote));

    useEffect(() => {
        router.events?.on('routeChangeStart', handleLoading(5));
        router.events?.on('routeChangeComplete', handleLoading(-5));

        return () => {
            router.events?.off('routeChangeStart', handleLoading(5));
            router.events?.off('routeChangeComplete', handleLoading(-5));
        };
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);

        if (!isBasketEmpty) {
            pushDeliveryPageLoad(carDetails, paymentJourneyType, isEC41Journey);
            pushCheckoutEvent(1, carDetails);
        }
    }, [carDetails, paymentJourneyType, isBasketEmpty]);

    return (
        <Main title={t('checkout.tabTitle')} checkout>
            {isBasketEmpty ? (
                <RedirectionModal />
            ) : (
                <>
                    <CheckoutStepWizard actualState="delivery" stock={carDetails.stock} />
                    <CheckoutDelivery dispatchToStore={dispatch} goToNextPage={goToMyDetails} />
                </>
            )}
        </Main>
    );
}

Checkout.getInitialProps = async (ctx: ContextWithStore) => {
    const { store, query, req, res, featureContext } = await updateGeneralContext(ctx);
    const isFinance = FilterDuck.getOwnState(store.getState()).budgetType === FINANCE;
    const checkoutUrl = isFinance ? CHECKOUT_FINANCE_URL : CHECKOUT_CASH_URL;
    const isUserLoggedIn = UserDuck.getOwnState(store.getState()).userAuth.isLoggedIn;

    try {
        const token = req?.cookies?.[SESSION_COOKIE_NAME] ?? getSessionIdCookie();

        let currentDeal = carDetailsDuck.getCurrentDeal(store.getState());

        if (currentDeal?.mopId && currentDeal?.extraFields?.useExternalCheckout) {
            return res.redirect(constructPsaCheckoutUrl(checkoutUrl, currentDeal?.mopId, isUserLoggedIn));
        }

        if (!currentDeal?.token) {
            currentDeal = (await DealService.currentDeal(token, false, false)).data;
            batch(() => {
                store.dispatch(carDetailsDuck.setCurrentDeal(currentDeal));
                store.dispatch(
                    FilterDuck.changeBudgetType(
                        currentDeal.journeyType === MySavedDealJourneyType.BUY_ONLINE_CASH ? CASH : FINANCE
                    )
                );
                store.dispatch(carDetailsDuck.setCurrentCarDetails(currentDeal.fullProductConfiguration));
            });
        }

        const pxVersion = featureContext.FEATURE_SWITCH_PX_VERSION;

        if (pxVersion === PxVersion.v2 && currentDeal?.partExchangeId) {
            const { data } = await syncPartExchange(currentDeal?.token);
            store.dispatch(ConfiguratorDuck.setPxState(processMopData(data?.data)));
        }

        await handleFinanceQuote(store, currentDeal.financeQuoteId);
    } catch (e: any) {
        console.error(e);
    }
    return { query, namespacesRequired: ['common'] };
};

export default Checkout;
